import React, { useEffect, useState, useRef } from "react";
import {
  Home,
  LayoutDashboard,
  FolderKanban,
  Folder,
  ChevronDown,
  BarChart3,
  Settings,
  HelpCircle,
  ExternalLink,
  Search,
  FilePlus2,
  ListChecks,
  FolderSearch,
  FileText,
  ShoppingCart,
  ClipboardList,
  FileCheck,
  FileBarChart,
  Menu,
  X,
  Users,
  LogOut,
  Shield,
  Activity,
  User,
  Mail,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import API_BASE_URL from "../config";
import logo from "../Image/logo1.png";

export default function Sidebar({ currentPage, setCurrentPage, sidebarCollapsed, setSidebarCollapsed, onLogout }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [allowedPositionNames, setAllowedPositionNames] = useState([]); // Store NAMES from API
  const [loading, setLoading] = useState(true);
  const [websiteDetails, setWebsiteDetails] = useState({
    websiteName: "PRPO",
    websiteSlogan: "Management System",
    logoUrl: null
  });

  // Fetch website details
  useEffect(() => {
    const fetchWebsiteDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}api/Website_Details`);
        if (!response.ok) throw new Error("Failed to fetch website details");

        const data = await response.json();
        if (data && data.length > 0) {
          const details = data[0];
          setWebsiteDetails({
            websiteName: details.websiteName || "PRPO",
            websiteSlogan: details.websiteSlogan || "Management System",
            logoUrl: details.hasLogo ? `${API_BASE_URL}api/Website_Details/logo/${details.id}` : null
          });
        }
      } catch (error) {
        console.error("❌ Failed to fetch website details:", error);
      }
    };

    fetchWebsiteDetails();
  }, []);

  const [sidebarPosition, setSidebarPosition] = useState(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const savedPosition = localStorage.getItem('sidebarPosition');

    if (isMobile && (savedPosition === 'left' || savedPosition === 'right')) {
      return 'top';
    }

    return savedPosition || (isMobile ? 'top' : 'left');
  });

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      if (isMobile && (sidebarPosition === 'left' || sidebarPosition === 'right')) {
        setSidebarPosition('top');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarPosition]);

  const [showPositionMenu, setShowPositionMenu] = useState(false);
  const [showHorizontalMenu, setShowHorizontalMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const positionMenuRef = useRef(null);
  const horizontalMenuRef = useRef(null);

  const toggleMenu = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  // 🔥 FIXED: New access control function using positionNames from API
  const hasAccess = (featureName) => {


    // If no allowed positions loaded yet
    if (!allowedPositionNames || allowedPositionNames.length === 0) {

      return false;
    }

    // 🔥 SPECIAL: Parent menu mappings - check if user has ANY related permission
    const featureToPositionMap = {
      // Main menus
      "Dashboard": ["Dashboard"],
      "Settings": ["Settings"],
      "Support": ["Support"],

      // Purchase Request children
      "Create Purchase Req": ["Create Purchase Req"],
      "My Requests": ["My Requests"],

      // Purchase Order children
      "My PO": ["My PO"],
      "MY Purchase Order": ["MY Purchase Order"],
      "PO Receiving": ["PO Receiving"],
      "Invoice Transmittal": ["Invoice Transmittal"],

      // Other
      "Quotation": ["Quotation"],
      "GoodsReceipt": ["GoodsReceipt"],

      // Approval children
      "PR Approval": ["PR Approval"],
      "Quotations Approval": ["Quotations Approval"],
      "PO Approval": ["PO Approval"],
      "PR Approval History": ["PR Approval History"],
      "PO Approval History": ["PO Approval History"],
      "SecondPR_Approval": ["SecondPR_Approval", "2nd PR Approval"],

      // Reporting children
      "PR Reporting": ["PR Reporting"],
      "PO Reporting": ["PO Reporting"],

      // Maintenance children
      "References": ["References", "Maintenance"],
      "UserManagement": ["UserManagement", "User Management", "Maintenance"],
      "InventoryDestinationManager": ["InventoryDestinationManager", "Inventory Destination", "Maintenance"],
      "InventoryDistribution": ["InventoryDistribution", "Inventory Distribution", "Maintenance"],

      // PARENT MENUS - show if user has ANY of the children
      "Purchase Request": ["Create Purchase Req", "My Requests", "Purchase Request"],
      "Purchase Order": ["My PO", "MY Purchase Order", "PO Receiving", "Invoice Transmittal", "Purchase Order"],
      "Approval": ["PR Approval", "Quotations Approval", "PO Approval", "PR Approval History", "PO Approval History", "SecondPR_Approval", "2nd PR Approval", "Approval"],
      "Reporting": ["PR Reporting", "PO Reporting", "Reporting"],
      "Maintenance": ["References", "UserManagement", "User Management", "InventoryDestinationManager", "InventoryDistribution", "Maintenance"],
    };

    const requiredPositions = featureToPositionMap[featureName] || [featureName];

    // Check if user has ANY of the required positions
    const hasPermission = requiredPositions.some(reqPos =>
      allowedPositionNames.some(allowedPos =>
        allowedPos.toLowerCase().trim() === reqPos.toLowerCase().trim()
      )
    );


    return hasPermission;
  };

  const menuItems = [
    { name: "Dashboard", page: "dashboard", category: "main", access: "Dashboard" },
    { name: "Create Purchase Req.", page: "CreatePO", category: "Purchase Request", access: "Create Purchase Req" },
    { name: "My Requests", page: "MyRequestsPage", category: "Purchase Request", access: "My Requests" },
    { name: "Quotation", page: "UnderMaintenancePage", category: "Quotation", access: "Quotation" },
    { name: "My PO", page: "PurchaseOrder", category: "Purchase Order", access: "My PO" },
    { name: "MY Purchase Order", page: "PurchaseOrdersPage", category: "Purchase Order", access: "MY Purchase Order" },
    { name: "PO Receiving", page: "POReceiving", category: "Purchase Order", access: "PO Receiving" },
    { name: "Invoice Transmittal", page: "POInvoiceTransmittal", category: "Purchase Order", access: "Invoice Transmittal" },
    { name: "GoodsReceipt", page: "GoodsReceipt", category: "GoodsReceipt", access: "GoodsReceipt" },
    { name: "PR Approval", page: "PRApproval", category: "Approval", access: "PR Approval" },
    { name: "Quotations Approval", page: "UnderMaintenancePage", category: "Approval", access: "Quotations Approval" },
    { name: "PO Approval", page: "POApproval", category: "Approval", access: "PO Approval" },
    { name: "PR Approval History", page: "PRApprovalHistory", category: "Approval", access: "PR Approval History" },
    { name: "PO Approval History", page: "POApprovalHistory", category: "Approval", access: "PO Approval History" },
    { name: "PR Reporting", page: "PR_Reports", category: "Reporting", access: "PR Reporting" },
    { name: "PO Reporting", page: "PO_Reports", category: "Reporting", access: "PO Reporting" },
    { name: "References", page: "referencess", category: "Maintenance", access: "References" },
    { name: "User Management", page: "UserManagement", category: "Maintenance", access: "UserManagement" },
    { name: "Settings", page: "Settingss", category: "main", access: "Settings" },
    { name: "Support", page: "UnderMaintenancePage", category: "main", access: "Support" },
    { name: "InventoryDestinationManager", page: "InventoryDestinationManager", category: "InventoryDestinationManager", access: "InventoryDestinationManager" },
    { name: "InventoryDistribution", page: "InventoryDistribution", category: "InventoryDistribution", access: "InventoryDistribution" },
    { name: "SecondPR_Approval", page: "SecondPR_Approval", category: "SecondPR_Approval", access: "SecondPR_Approval" },
  ];

  const filteredMenuItems = menuItems.filter(item => {
    if (!searchQuery) return false;
    const query = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(query) ||
      item.category.toLowerCase().includes(query)
    ) && hasAccess(item.access);
  });

  useEffect(() => {
    localStorage.setItem('sidebarPosition', sidebarPosition);
  }, [sidebarPosition]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      console.log("⚠️ No user in localStorage");
      setLoading(false);
      return;
    }

    const userData = JSON.parse(storedUser);
    setUser(userData);

    const { userId, userLevelId } = userData;

    console.log("=== SIDEBAR LOADING ===");
    console.log("User Data:", userData);
    console.log("User Level ID:", userLevelId);
    console.log("Position ID:", userData.positionId);

    // Fetch profile image
    // Fetch profile image
    fetch(`${API_BASE_URL}api/Users/${userId}/profile`)
      .then(async (res) => {
        if (!res.ok) return; // silent — walang console error kung 404

        const contentType = res.headers.get("content-type");

        if (contentType && contentType.startsWith("application/json")) return;

        const imageBlob = await res.blob();
        if (!imageBlob || imageBlob.size === 0) return;

        const imageUrl = URL.createObjectURL(imageBlob);
        setUser((prev) => ({ ...prev, profileImage: imageUrl }));
      })
      .catch(() => { }); // silent catch — no console.error

    // Fetch user level name
    if (userLevelId) {
      fetch(`${API_BASE_URL}api/UserLevels/${userLevelId}`)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          return res.json();
        })
        .then((levelData) => {
          console.log("✅ User Level Data:", levelData);
          setUser((prev) => ({ ...prev, userLevelName: levelData.userLevelName }));
        })
        .catch((err) => console.error("❌ Failed to fetch user level:", err));
    }

    if (!userLevelId) {
      console.log("⚠️ userLevelId missing");
      setAllowedPositionNames([]);
      setLoading(false);
      return;
    }

    // 🔥 FIXED: Fetch role access and use positionNames
    fetch(`${API_BASE_URL}api/Role_Position_Access/by-user-level/${userLevelId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then((data) => {


        // Use positionNames from API response
        const positionNames = data.positionNames || [];
        setAllowedPositionNames(positionNames);

        setLoading(false);
      })
      .catch((err) => {
        console.error("❌ Failed to fetch role access:", err);
        setAllowedPositionNames([]);
        setLoading(false);
      });

  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (positionMenuRef.current && !positionMenuRef.current.contains(event.target)) {
        setShowPositionMenu(false);
      }
      if (horizontalMenuRef.current && !horizontalMenuRef.current.contains(event.target)) {
        setShowHorizontalMenu(false);
      }
    }

    function handleEsc(event) {
      if (event.key === "Escape") {
        setUserMenuOpen(false);
        setShowPositionMenu(false);
        setShowHorizontalMenu(false);
        setSearchQuery("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const isHorizontal = sidebarPosition === 'top' || sidebarPosition === 'bottom';

  const getSidebarStyles = () => {
    const baseStyles = "fixed bg-gradient-to-br from-slate-50 via-white to-blue-50 border-gray-200/80 flex transform transition-all duration-300 shadow-xl z-40";

    if (isHorizontal) {
      switch (sidebarPosition) {
        case 'top':
          return `${baseStyles} top-0 left-0 right-0 h-16 border-b flex-row translate-y-0`;
        case 'bottom':
          return `${baseStyles} bottom-0 left-0 right-0 h-16 border-t flex-row translate-y-0`;
      }
    }

    if (sidebarCollapsed) {
      switch (sidebarPosition) {
        case 'left':
          return `${baseStyles} top-0 left-0 h-screen w-64 border-r flex-col md:-translate-x-full -translate-x-full`;
        case 'right':
          return `${baseStyles} top-0 right-0 h-screen w-64 border-l flex-col md:translate-x-full translate-x-full`;
      }
    }

    switch (sidebarPosition) {
      case 'left':
        return `${baseStyles} top-0 left-0 h-screen w-64 border-r flex-col ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`;
      case 'right':
        return `${baseStyles} top-0 right-0 h-screen w-64 border-l flex-col ${sidebarOpen ? "translate-x-0" : "translate-x-full"} md:translate-x-0`;
      default:
        return `${baseStyles} top-0 left-0 h-screen w-64 border-r flex-col`;
    }
  };

  const getToggleButtonPosition = () => {
    if (sidebarCollapsed) {
      switch (sidebarPosition) {
        case 'left': return 'top-4 left-4';
        case 'right': return 'top-4 right-4';
        case 'top': return 'top-4 right-4';
        case 'bottom': return 'bottom-4 right-4';
      }
    }
    switch (sidebarPosition) {
      case 'left': return 'top-4 left-60';
      case 'right': return 'top-4 right-60';
      case 'top': return 'top-4 right-4';
      case 'bottom': return 'bottom-4 right-4';
      default: return 'top-4 left-60';
    }
  };

  const getGearButtonPosition = () => {
    if (isHorizontal) {
      switch (sidebarPosition) {
        case 'top': return 'top-4 right-16';
        case 'bottom': return 'bottom-4 right-16';
        default: return 'top-4 right-16';
      }
    }
    switch (sidebarPosition) {
      case 'left': return 'bottom-20 left-60';
      case 'right': return 'bottom-20 right-60';
      default: return 'bottom-20 left-4';
    }
  };

  const getPositionMenuPlacement = () => {
    switch (sidebarPosition) {
      case 'left': return 'left-16 bottom-0';
      case 'right': return 'right-16 bottom-0';
      case 'top': return 'top-16 right-0';
      case 'bottom': return 'bottom-16 right-0';
      default: return 'left-16 bottom-0';
    }
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 h-screen w-64 bg-gradient-to-br from-slate-50 via-white to-blue-50 border-r border-gray-200/80 flex items-center justify-center z-40">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow { animation: spin-slow 3s linear infinite; }

        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { 
          background: linear-gradient(to bottom, #818cf8, #3b82f6); 
          border-radius: 10px; 
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { 
          background: linear-gradient(to bottom, #6366f1, #2563eb); 
        }
      `}</style>

      <div className="flex">
        {sidebarOpen && !isHorizontal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden transition-opacity duration-300"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {!isHorizontal && (
          <button
            className={`p-3 md:hidden fixed z-50 bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-xl rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 ${sidebarPosition === 'right' ? 'top-3 right-3' : 'top-3 left-3'}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        )}

        {!isHorizontal && (
          <button
            className={`hidden md:flex items-center justify-center fixed ${getToggleButtonPosition()} z-50 bg-gradient-to-r from-indigo-600 to-blue-600 text-white w-10 h-10 rounded-full shadow-xl border border-white hover:shadow-2xl hover:scale-110 transition-all duration-300`}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
        )}

        <div className={getSidebarStyles()}>
          {isHorizontal ? (
            <>
              {/* Logo Section */}
              <div className="px-4 py-2 border-r border-gray-200/80 bg-gradient-to-r from-indigo-600 to-blue-600 flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg overflow-hidden shadow-lg ring-2 ring-white/30 bg-white flex-shrink-0">
                  <img
                    src={websiteDetails.logoUrl || logo}
                    alt="Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-white hidden lg:block">
                  <h1 className="font-bold text-lg">{websiteDetails.websiteName}</h1>
                  <p className="text-xs text-blue-100">{websiteDetails.websiteSlogan}</p>
                </div>
              </div>

              {/* Menu Button */}
              <div className="px-3 flex items-center relative" ref={horizontalMenuRef}>
                <button
                  onClick={() => setShowHorizontalMenu(!showHorizontalMenu)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-gray-700 hover:text-indigo-600"
                >
                  <Menu className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">Menu</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showHorizontalMenu ? 'rotate-180' : ''}`} />
                </button>

                {showHorizontalMenu && (
                  <div className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} left-0 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[80vh] overflow-y-auto custom-scrollbar animate-fadeIn z-50`}>
                    <div className="p-2 space-y-1">
                      {hasAccess("Dashboard") && (
                        <NavItem
                          icon={<LayoutDashboard className="w-4 h-4" />}
                          label="Dashboard"
                          onClick={() => {
                            setCurrentPage && setCurrentPage("dashboard");
                            setShowHorizontalMenu(false);
                          }}
                          active={currentPage === "dashboard"}
                        />
                      )}


                      {hasAccess("Dashboard") && (
                        <NavItem
                          icon={<LayoutDashboard className="w-4 h-4" />}
                          label="CombinedDashboard"
                          onClick={() => {
                            setCurrentPage && setCurrentPage("CombinedDashboard");
                            setShowHorizontalMenu(false);
                          }}
                          active={currentPage === "Dashboard"}
                        />
                      )}


                      {hasAccess("Purchase Request") && (
                        <Dropdown
                          label="Purchase Request"
                          icon={<FolderKanban className="w-4 h-4" />}
                          open={openMenu === "pr"}
                          onClick={() => toggleMenu("pr")}
                          items={[
                            hasAccess("Create Purchase Req") && {
                              icon: <FilePlus2 className="w-4 h-4" />,
                              label: "Create Purchase Req.",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("CreatePO");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "CreatePO",
                            },
                            hasAccess("My Requests") && {
                              icon: <ListChecks className="w-4 h-4" />,
                              label: "My Requests",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("MyRequestsPage");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "MyRequestsPage",
                            },
                          ].filter(Boolean)}
                        />
                      )}

                      {hasAccess("Quotation") && (
                        <Dropdown
                          label="Quotation"
                          icon={<FileText className="w-4 h-4" />}
                          open={openMenu === "quotation"}
                          onClick={() => toggleMenu("quotation")}
                          items={[
                            {
                              icon: <ListChecks className="w-4 h-4" />,
                              label: "Quotation",
                              onClick: () => {
                                setCurrentPage("UnderMaintenancePage");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "UnderMaintenancePage",
                            },
                          ]}
                        />
                      )}

                      {hasAccess("Purchase Order") && (
                        <Dropdown
                          label="Purchase Order"
                          icon={<ShoppingCart className="w-4 h-4" />}
                          open={openMenu === "po"}
                          onClick={() => toggleMenu("po")}
                          items={[
                            hasAccess("My PO") && {
                              icon: <FileCheck className="w-4 h-4" />,
                              label: "Purchase Order",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("PurchaseOrder");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "PurchaseOrder",
                            },
                            hasAccess("MY Purchase Order") && {
                              icon: <ClipboardList className="w-4 h-4" />,
                              label: "MY Purchase Order",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("PurchaseOrdersPage");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "PurchaseOrdersPage",
                            },
                            hasAccess("PO Receiving") && {
                              icon: <FileBarChart className="w-4 h-4" />,
                              label: "PO Receiving",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("POReceiving");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "POReceiving",
                            },
                            hasAccess("Invoice Transmittal") && {
                              icon: <FileBarChart className="w-4 h-4" />,
                              label: "Invoice Transmittal",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("POInvoiceTransmittal");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "POInvoiceTransmittal",
                            },
                          ].filter(Boolean)}
                        />
                      )}

                      {hasAccess("GoodsReceipt") && (
                        <NavItem
                          icon={<FileText className="w-5 h-5" />}
                          label="Goods Receipt (GR)"
                          onClick={() => {
                            setCurrentPage && setCurrentPage("GoodsReceipt");
                            setShowHorizontalMenu(false);
                          }}
                          active={currentPage === "GoodsReceipt"}
                        />
                      )}

                      {hasAccess("Approval") && (
                        <Dropdown
                          label="Approval"
                          icon={<FileCheck className="w-4 h-4" />}
                          open={openMenu === "approval"}
                          onClick={() => toggleMenu("approval")}
                          items={[
                            hasAccess("PR Approval") && {
                              icon: <FileCheck className="w-4 h-4" />,
                              label: "PR Approval",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("PRApproval");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "PRApproval",
                            },
                            hasAccess("Quotations Approval") && {
                              icon: <FileCheck className="w-4 h-4" />,
                              label: "Quotations Approval",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("UnderMaintenancePage");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "UnderMaintenancePage",
                            },
                            hasAccess("PO Approval") && {
                              icon: <FileCheck className="w-4 h-4" />,
                              label: "PO Approval",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("POApproval");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "POApproval",
                            },
                            hasAccess("PR Approval History") && {
                              icon: <FileCheck className="w-4 h-4" />,
                              label: "PR Approval History",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("PRApprovalHistory");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "PRApprovalHistory",
                            },
                            hasAccess("PO Approval History") && {
                              icon: <FileCheck className="w-4 h-4" />,
                              label: "PO Approval History",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("POApprovalHistory");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "POApprovalHistory",
                            },
                            hasAccess("SecondPR_Approval") && {
                              icon: <FileCheck className="w-4 h-4" />,
                              label: "2nd PR Approval",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("SecondPR_Approval");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "SecondPR_Approval",
                            },
                          ].filter(Boolean)}
                        />
                      )}

                      {hasAccess("Reporting") && (
                        <Dropdown
                          label="Reporting"
                          icon={<BarChart3 className="w-4 h-4" />}
                          open={openMenu === "reporting"}
                          onClick={() => toggleMenu("reporting")}
                          items={[
                            hasAccess("PR Reporting") && {
                              icon: <FileCheck className="w-4 h-4" />,
                              label: "PR Reporting",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("PR_Reports");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "PR_Reports",
                            },
                            hasAccess("PO Reporting") && {
                              icon: <FileCheck className="w-4 h-4" />,
                              label: "PO Reporting",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("PO_Reports");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "PO_Reports",
                            },
                          ].filter(Boolean)}
                        />
                      )}

                      {hasAccess("Maintenance") && (
                        <Dropdown
                          label="Maintenance"
                          icon={<Settings className="w-4 h-4" />}
                          open={openMenu === "maintenance"}
                          onClick={() => toggleMenu("maintenance")}
                          items={[
                            hasAccess("References") && {
                              icon: <FilePlus2 className="w-4 h-4" />,
                              label: "References",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("referencess");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "referencess",
                            },
                            hasAccess("UserManagement") && {
                              icon: <Users className="w-4 h-4" />,
                              label: "User Management",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("UserManagement");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "UserManagement",
                            },
                            hasAccess("InventoryDestinationManager") && {
                              icon: <Users className="w-4 h-4" />,
                              label: "Inventory Destination",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("InventoryDestinationManager");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "InventoryDestinationManager",
                            },
                            hasAccess("InventoryDistribution") && {
                              icon: <Users className="w-4 h-4" />,
                              label: "Inventory Distribution",
                              onClick: () => {
                                setCurrentPage && setCurrentPage("InventoryDistribution");
                                setShowHorizontalMenu(false);
                              },
                              active: currentPage === "InventoryDistribution",
                            },
                          ].filter(Boolean)}
                        />
                      )}

                      {hasAccess("Settings") && (
                        <NavItem
                          icon={<Settings className="w-4 h-4" />}
                          label="Settings"
                          onClick={() => {
                            setCurrentPage && setCurrentPage("Settingss");
                            setShowHorizontalMenu(false);
                          }}
                          active={currentPage === "Settingss"}
                        />
                      )}

                      {hasAccess("Support") && (
                        <NavItem
                          icon={<HelpCircle className="w-4 h-4" />}
                          label="Support"
                          onClick={() => {
                            setCurrentPage && setCurrentPage("UnderMaintenancePage");
                            setShowHorizontalMenu(false);
                          }}
                          active={currentPage === "UnderMaintenancePages"}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Search Bar */}
              <div className="px-3 flex items-center flex-1 max-w-md relative">
                <div className="relative w-full group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  <input
                    type="text"
                    placeholder="Search menu..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm hover:shadow-md"
                  />

                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}

                  {searchQuery && filteredMenuItems.length > 0 && (
                    <div className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto custom-scrollbar z-50 animate-fadeIn`}>
                      <div className="p-2 space-y-1">
                        {filteredMenuItems.map((item, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setCurrentPage && setCurrentPage(item.page);
                              setSearchQuery("");
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors text-left ${currentPage === item.page ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                          >
                            <div>
                              <div className="text-sm font-medium">{item.name}</div>
                              <div className="text-xs text-gray-500">{item.category}</div>
                            </div>
                            <ChevronDown className="w-4 h-4 -rotate-90" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {searchQuery && filteredMenuItems.length === 0 && (
                    <div className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-2xl p-3 z-50 animate-fadeIn`}>
                      <p className="text-sm text-gray-500 text-center">No results found</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1"></div>

              {/* User Profile Section */}
              {user && (
                <div className="px-3 border-l border-gray-200/80 flex items-center relative">
                  <button
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                  >
                    <img
                      src={user.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.userId}`}
                      alt="Profile"
                      className="w-8 h-8 rounded-full ring-2 ring-white shadow-md"
                    />
                    <span className="text-sm font-medium text-gray-700 hidden md:inline">
                      {user.firstName}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 hidden md:inline transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {userMenuOpen && (
                    <div
                      ref={dropdownRef}
                      className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} right-0 w-72 bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden z-50 animate-slideUp`}
                    >
                      <div className={`absolute ${sidebarPosition === 'top' ? 'top-0 right-6 transform -translate-y-1/2' : 'bottom-0 right-6 transform translate-y-1/2'} w-4 h-4 bg-white border-${sidebarPosition === 'top' ? 't' : 'b'} border-${sidebarPosition === 'top' ? 'l' : 'r'} border-gray-200 rotate-45`}></div>

                      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-4 text-white">
                        <div className="flex items-center gap-3">
                          <img
                            src={user.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.userId}`}
                            alt="Profile"
                            className="w-14 h-14 rounded-full ring-2 ring-white/30 shadow-lg"
                          />
                          <div>
                            <p className="font-bold text-base">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-xs text-blue-100">@{user.username || "user"}</p>
                          </div>
                        </div>
                      </div>

                      <div className="p-2 space-y-1">
                        <button
                          onClick={() => {
                            setCurrentPage && setCurrentPage("MyProfile");
                            setUserMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg transition-colors group">
                          <User className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" />
                          <span className="font-medium">My Profile</span>
                        </button>

                        <div className="px-3 py-2.5 text-sm text-gray-600 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</span>
                          </div>
                          <p className="text-sm font-medium text-gray-800 ml-7">{user.email}</p>
                        </div>

                        <div className="px-3 py-2.5 text-sm text-gray-600 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <Shield className="w-4 h-4 text-gray-400" />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</span>
                          </div>
                          <p className="text-sm font-medium text-gray-800 ml-7">
                            {user.positionId || user.userLevelName || "Loading..."}
                          </p>
                        </div>

                        <div className="px-3 py-2.5 text-sm text-gray-600 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Activity className="w-4 h-4 text-green-500" />
                            <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">Status</span>
                            <div className="ml-auto flex items-center gap-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                              <span className="text-sm font-medium text-green-700">Online</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-2 border-t border-gray-200">
                        <button
                          onClick={onLogout}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all font-semibold group"
                        >
                          <LogOut className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {/* VERTICAL LAYOUT - Same structure as before but using new hasAccess logic */}
              <div className="p-6 border-b border-gray-200/80 bg-gradient-to-r from-indigo-600 to-blue-600">
                <div className="flex items-center gap-3">
                  <div className="w-13 h-12 rounded-xl overflow-hidden shadow-lg ring-2 ring-white/30 bg-white">
                    <img src={logo} alt="Logo" className="w-full h-full object-cover" />
                  </div>
                  <div className="text-white">
                    <h1 className="font-bold text-lg">{websiteDetails.websiteName}</h1>
                    <p className="text-xs text-blue-100">{websiteDetails.websiteSlogan}</p>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="relative group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  <input
                    type="text"
                    placeholder="Search menu..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm hover:shadow-md"
                  />

                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}

                  {searchQuery && filteredMenuItems.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto custom-scrollbar z-50 animate-fadeIn">
                      <div className="p-2 space-y-1">
                        {filteredMenuItems.map((item, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setCurrentPage && setCurrentPage(item.page);
                              setSearchQuery("");
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors text-left ${currentPage === item.page ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                          >
                            <div>
                              <div className="text-sm font-medium">{item.name}</div>
                              <div className="text-xs text-gray-500">{item.category}</div>
                            </div>
                            <ChevronDown className="w-4 h-4 -rotate-90" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {searchQuery && filteredMenuItems.length === 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl p-4 z-50 animate-fadeIn">
                      <p className="text-sm text-gray-500 text-center">No results found for "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              </div>

              <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto custom-scrollbar">
                {hasAccess("Dashboard") && (
                  <NavItem
                    icon={<LayoutDashboard className="w-5 h-5" />}
                    label="Dashboard"
                    onClick={() => setCurrentPage && setCurrentPage("dashboard")}
                    active={currentPage === "dashboard"}
                  />
                )}
                {hasAccess("ItemDashboard") && (
                  <NavItem
                    icon={<LayoutDashboard className="w-4 h-4" />}
                    label="Item Dashboard"
                    onClick={() => {
                      setCurrentPage && setCurrentPage("CombinedDashboard");
                      setShowHorizontalMenu(false);
                    }}
                    active={currentPage === "ItemDashboard"}
                  />
                )}

                {hasAccess("Purchase Request") && (
                  <Dropdown
                    label="Purchase Request"
                    icon={<FolderKanban className="w-5 h-5" />}
                    open={openMenu === "pr"}
                    onClick={() => toggleMenu("pr")}
                    items={[
                      hasAccess("Create Purchase Req") && {
                        icon: <FilePlus2 className="w-4 h-4" />,
                        label: "Create Purchase Req.",
                        onClick: () => setCurrentPage && setCurrentPage("CreatePO"),
                        active: currentPage === "CreatePO",
                      },
                      hasAccess("PurchaseRequests") && {
                        icon: <ListChecks className="w-4 h-4" />,
                        label: "My Requests",
                        onClick: () => setCurrentPage && setCurrentPage("PurchaseRequests"),
                        active: currentPage === "PurchaseRequests",
                      },
                    ].filter(Boolean)}
                  />
                )}

                {hasAccess("Quotation") && (
                  <Dropdown
                    label="Quotation"
                    icon={<FileText className="w-5 h-5" />}
                    open={openMenu === "quotation"}
                    onClick={() => toggleMenu("quotation")}
                    items={[
                      {
                        icon: <ListChecks className="w-4 h-4" />,
                        label: "Quotation",
                        onClick: () => setCurrentPage("UnderMaintenancePage"),
                        active: currentPage === "UnderMaintenancePage",
                      },
                    ]}
                  />
                )}

                {hasAccess("Purchase Order") && (
                  <Dropdown
                    label="Purchase Order"
                    icon={<ShoppingCart className="w-5 h-5" />}
                    open={openMenu === "po"}
                    onClick={() => toggleMenu("po")}
                    items={[
                      hasAccess("My PO") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Purchase Order",
                        onClick: () => setCurrentPage && setCurrentPage("PurchaseOrder"),
                        active: currentPage === "PurchaseOrder",
                      },
                      hasAccess("MY Purchase Order") && {
                        icon: <ClipboardList className="w-4 h-4" />,
                        label: "MY Purchase Order",
                        onClick: () => setCurrentPage && setCurrentPage("PurchaseOrdersPage"),
                        active: currentPage === "PurchaseOrdersPage",
                      },
                      hasAccess("PO Receiving") && {
                        icon: <FileBarChart className="w-4 h-4" />,
                        label: "PO Receiving",
                        onClick: () => setCurrentPage && setCurrentPage("POReceiving"),
                        active: currentPage === "POReceiving",
                      },
                      hasAccess("Invoice Transmittal") && {
                        icon: <FileBarChart className="w-4 h-4" />,
                        label: "Invoice Transmittal",
                        onClick: () => setCurrentPage && setCurrentPage("POInvoiceTransmittal"),
                        active: currentPage === "POInvoiceTransmittal",
                      },
                    ].filter(Boolean)}
                  />
                )}







                {hasAccess("GoodsReceipt") && (
                  <Dropdown
                    label="Goods Receipt (GR)"
                    icon={<FileText className="w-5 h-5" />}
                    open={openMenu === "GoodsReceipt"}
                    onClick={() => toggleMenu("GoodsReceipt")}
                    items={[
                      hasAccess("GoodsReceipt") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "GoodsReceipt",
                        onClick: () => setCurrentPage && setCurrentPage("GoodsReceipt"),
                        active: currentPage === "PR_Reports",
                      },
                      hasAccess("GRViewList") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "GRViewList",
                        onClick: () => setCurrentPage && setCurrentPage("GRViewList"),
                        active: currentPage === "GRViewList",
                      },

                    ].filter(Boolean)}
                  />
                )}


                {hasAccess("InventoryList") && (
                  <Dropdown
                    label="Inventory"
                    icon={<FileText className="w-5 h-5" />}
                    open={openMenu === "InventoryList"}
                    onClick={() => toggleMenu("InventoryList")}
                    items={[
                      hasAccess("InventoryList") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "InventoryList",
                        onClick: () => setCurrentPage && setCurrentPage("InventoryList"),
                        active: currentPage === "InventoryList",
                      },
                      hasAccess("StockAdjustmentForm") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Stock Adjustment ",
                        onClick: () => setCurrentPage && setCurrentPage("StockAdjustmentForm"),
                        active: currentPage === "StockAdjustmentForm",
                      },
                      hasAccess("StockAdjustmentList") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Stock Adjustment List",
                        onClick: () => setCurrentPage && setCurrentPage("StockAdjustmentList"),
                        active: currentPage === "StockAdjustmentList",
                      },
                    ].filter(Boolean)}
                  />
                )}
                {hasAccess("MaterialRequestForm") && (
                  <Dropdown
                    label="Item Request Form"
                    icon={<FileText className="w-5 h-5" />}
                    open={openMenu === "MaterialRequestForm"}
                    onClick={() => toggleMenu("MaterialRequestForm")}
                    items={[
                      hasAccess("MaterialRequestForm") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Item Request Form",
                        onClick: () => setCurrentPage && setCurrentPage("MaterialRequestForm"),
                        active: currentPage === "MaterialRequestForm",
                      },
                      hasAccess("ItemRequest_GI") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Item Request Viewer",
                        onClick: () => setCurrentPage && setCurrentPage("ShipToShipRequestViewer"),
                        active: currentPage === "ShipToShipRequestViewer",
                      },
                      hasAccess("MaterialRequestList") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "MR Folder ByDate",
                        onClick: () => setCurrentPage && setCurrentPage("MRFolderByDate"),
                        active: currentPage === "MRFolderByDate",
                      },



                      hasAccess("MaterialRequestList") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Goods Issue ",
                        onClick: () => setCurrentPage && setCurrentPage("MaterialRequestList"),
                        active: currentPage === "MaterialRequestList",
                      },







                    ].filter(Boolean)}
                  />
                )}

                {hasAccess("FuelRequestForm") && (
                  <Dropdown
                    label="Fuel Request Form"
                    icon={<FileText className="w-5 h-5" />}
                    open={openMenu === "FuelRequestForm"}
                    onClick={() => toggleMenu("FuelRequestForm")}
                    items={[
                      hasAccess("FuelRequestForm") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Fuel Request Form",
                        onClick: () => setCurrentPage && setCurrentPage("FuelRequestForm"),
                        active: currentPage === "FuelRequestForm",
                      },
                      hasAccess("MaterialRequestList") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Fuel Requisition List ",
                        onClick: () => setCurrentPage && setCurrentPage("FuelRequisitionList"),
                        active: currentPage === "FuelRequisitionList",
                      },
                      hasAccess("FuelRequisitionApproval") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Fuel Requisition Approval ",
                        onClick: () => setCurrentPage && setCurrentPage("FuelRequisitionApproval"),
                        active: currentPage === "FuelRequisitionApproval",
                      },

                    ].filter(Boolean)}
                  />
                )}











                {hasAccess("Approval") && (
                  <Dropdown
                    label="Approval"
                    icon={<FileCheck className="w-5 h-5" />}
                    open={openMenu === "approval"}
                    onClick={() => toggleMenu("approval")}
                    items={[
                      hasAccess("PR Approval") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "PR Approval",
                        onClick: () => setCurrentPage && setCurrentPage("PRApproval"),
                        active: currentPage === "PRApproval",
                      },
                      hasAccess("Quotations Approval") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Quotations Approval",
                        onClick: () => setCurrentPage && setCurrentPage("UnderMaintenancePage"),
                        active: currentPage === "UnderMaintenancePage",
                      },
                      hasAccess("PO Approval") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "PO Approval",
                        onClick: () => setCurrentPage && setCurrentPage("POApproval"),
                        active: currentPage === "POApproval",
                      },
                      hasAccess("PR Approval History") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "PR Approval History",
                        onClick: () => setCurrentPage && setCurrentPage("PRApprovalHistory"),
                        active: currentPage === "PRApprovalHistory",
                      },
                      hasAccess("PO Approval History") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "PO Approval History",
                        onClick: () => setCurrentPage && setCurrentPage("POApprovalHistory"),
                        active: currentPage === "POApprovalHistory",
                      },
                      hasAccess("SecondPR_Approval") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "2nd PR Approval",
                        onClick: () => setCurrentPage && setCurrentPage("SecondPR_Approval"),
                        active: currentPage === "SecondPR_Approval",
                      },

                      hasAccess("MaterialRequestApproval") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Material Request Approval",
                        onClick: () => setCurrentPage && setCurrentPage("MaterialRequestApproval"),
                        active: currentPage === "MaterialRequestApproval",
                      },


                    ].filter(Boolean)}
                  />
                )}

                {hasAccess("Reporting") && (
                  <Dropdown
                    label="Reporting"
                    icon={<BarChart3 className="w-5 h-5" />}
                    open={openMenu === "reporting"}
                    onClick={() => toggleMenu("reporting")}
                    items={[
                      hasAccess("PR Reporting") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "PR Reporting",
                        onClick: () => setCurrentPage && setCurrentPage("PR_Reports"),
                        active: currentPage === "PR_Reports",
                      },
                      hasAccess("PO Reporting") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "PO Reporting",
                        onClick: () => setCurrentPage && setCurrentPage("PO_Reports"),
                        active: currentPage === "PO_Reports",
                      },

                      hasAccess("Rm Reports") && {
                        icon: <FileCheck className="w-4 h-4" />,
                        label: "Rm Reports",
                        onClick: () => setCurrentPage && setCurrentPage("RmReports"),
                        active: currentPage === "RmReports",
                      },


                    ].filter(Boolean)}
                  />
                )}

                {hasAccess("Maintenance") && (
                  <Dropdown
                    label="Maintenance"
                    icon={<Settings className="w-5 h-5" />}
                    open={openMenu === "maintenance"}
                    onClick={() => toggleMenu("maintenance")}
                    items={[
                      hasAccess("References") && {
                        icon: <FilePlus2 className="w-4 h-4" />,
                        label: "References",
                        onClick: () => setCurrentPage && setCurrentPage("referencess"),
                        active: currentPage === "referencess",
                      },
                      hasAccess("UserManagement") && {
                        icon: <Users className="w-4 h-4" />,
                        label: "User Management",
                        onClick: () => setCurrentPage && setCurrentPage("UserManagement"),
                        active: currentPage === "UserManagement",
                      },
                      hasAccess("InventoryDestinationManager") && {
                        icon: <Users className="w-4 h-4" />,
                        label: "Inventory Destination",
                        onClick: () => setCurrentPage && setCurrentPage("InventoryDestinationManager"),
                        active: currentPage === "InventoryDestinationManager",
                      },
                      hasAccess("InventoryDistribution") && {
                        icon: <Users className="w-4 h-4" />,
                        label: "Inventory Distribution",
                        onClick: () => setCurrentPage && setCurrentPage("InventoryDistribution"),
                        active: currentPage === "InventoryDistribution",
                      },
                    ].filter(Boolean)}
                  />
                )}

                {hasAccess("Settings") && (
                  <NavItem
                    icon={<Settings className="w-5 h-5" />}
                    label="Settings"
                    onClick={() => setCurrentPage && setCurrentPage("Settingss")}
                    active={currentPage === "Settingss"}
                  />
                )}

                <div className="relative">
                  {hasAccess("Support") && (
                    <NavItem
                      icon={<HelpCircle className="w-5 h-5" />}
                      label="Support"
                      onClick={() => setCurrentPage && setCurrentPage("UnderMaintenancePage")}
                      active={currentPage === "UnderMaintenancePages"}
                    />
                  )}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-gray-600 font-medium">Online</span>
                  </div>
                </div>

                <NavItem
                  icon={<ExternalLink className="w-5 h-5" />}
                  label="User Manual"
                  onClick={() => setCurrentPage && setCurrentPage("PRPOManual")}
                  active={currentPage === "PRPOManual"}
                />              </nav>

              <div className="mt-auto p-4 border-t border-gray-200/80 bg-gradient-to-r from-slate-50 to-blue-50 relative">
                {user ? (
                  <div className="relative">
                    <button
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 group"
                      onClick={() => setUserMenuOpen((prev) => !prev)}
                    >
                      <div className="relative">
                        <img
                          src={user.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.userId}`}
                          alt="Profile"
                          className="w-12 h-12 rounded-full ring-2 ring-white shadow-md group-hover:ring-indigo-400 transition-all"
                        />
                        <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                      </div>

                      <div className="flex flex-col flex-1 text-left">
                        <span className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                          {user.firstName} {user.lastName}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                          Online
                        </span>
                      </div>

                      <ChevronDown
                        className={`w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-all duration-300 ${userMenuOpen ? "rotate-180" : ""}`}
                      />
                    </button>

                    {userMenuOpen && (
                      <div
                        ref={dropdownRef}
                        className="absolute bottom-full left-0 mb-3 w-72 bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden z-50 animate-slideUp"
                      >
                        <div className="absolute bottom-0 left-6 transform translate-y-1/2 w-4 h-4 bg-white border-b border-r border-gray-200 rotate-45"></div>

                        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-4 text-white">
                          <div className="flex items-center gap-3">
                            <img
                              src={user.profileImage || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.userId}`}
                              alt="Profile"
                              className="w-14 h-14 rounded-full ring-2 ring-white/30 shadow-lg"
                            />
                            <div>
                              <p className="font-bold text-base">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-xs text-blue-100">@{user.username || "user"}</p>
                            </div>
                          </div>
                        </div>

                        <div className="p-2 space-y-1">
                          <button
                            onClick={() => {
                              setCurrentPage && setCurrentPage("MyProfile");
                              setUserMenuOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg transition-colors group">
                            <User className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" />
                            <span className="font-medium">My Profile</span>
                          </button>

                          <div className="px-3 py-2.5 text-sm text-gray-600 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3 mb-2">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</span>
                            </div>
                            <p className="text-sm font-medium text-gray-800 ml-7">{user.email}</p>
                          </div>

                          <div className="px-3 py-2.5 text-sm text-gray-600 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3 mb-2">
                              <Shield className="w-4 h-4 text-gray-400" />
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</span>
                            </div>
                            <p className="text-sm font-medium text-gray-800 ml-7">
                              {user.positionId || "Loading..."}
                            </p>
                          </div>

                          <div className="px-3 py-2.5 text-sm text-gray-600 bg-green-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Activity className="w-4 h-4 text-green-500" />
                              <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">Status</span>
                              <div className="ml-auto flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm font-medium text-green-700">Online</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="p-2 border-t border-gray-200">
                          <button
                            onClick={onLogout}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all font-semibold group"
                          >
                            <LogOut className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                            <span>Logout</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="text-sm text-gray-500 font-medium">Not logged in</span>
                  </div>
                )}
              </div>
            </>
          )}

          {!sidebarCollapsed && !isHorizontal && (
            <div className={`fixed ${getGearButtonPosition()} z-[9999]`}>
              <div className="relative" ref={positionMenuRef}>
                <button
                  onClick={() => setShowPositionMenu(!showPositionMenu)}
                  className="w-9 h-9 bg-gradient-to-r from-gray-600 to-gray-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
                >
                  <Settings className="w-5 h-5 animate-spin-slow" />
                </button>

                {showPositionMenu && (
                  <div className={`absolute ${getPositionMenuPlacement()} bg-white rounded-xl shadow-2xl border border-gray-200 p-2 space-y-1 min-w-[160px] z-[10000] animate-fadeIn`}>
                    <button
                      onClick={() => {
                        setSidebarPosition('left');
                        setShowPositionMenu(false);
                      }}
                      className={`hidden md:flex w-full items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === 'left' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="text-sm font-medium">Left</span>
                    </button>

                    <button
                      onClick={() => {
                        setSidebarPosition('right');
                        setShowPositionMenu(false);
                      }}
                      className={`hidden md:flex w-full items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === 'right' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                    >
                      <ArrowRight className="w-4 h-4" />
                      <span className="text-sm font-medium">Right</span>
                    </button>

                    <button
                      onClick={() => {
                        setSidebarPosition('top');
                        setShowPositionMenu(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === 'top' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                    >
                      <ArrowUp className="w-4 h-4" />
                      <span className="text-sm font-medium">Top</span>
                    </button>

                    <button
                      onClick={() => {
                        setSidebarPosition('bottom');
                        setShowPositionMenu(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === 'bottom' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                    >
                      <ArrowDown className="w-4 h-4" />
                      <span className="text-sm font-medium">Bottom</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {isHorizontal && (
            <div className="px-3 flex items-center relative" ref={positionMenuRef}>
              <button
                onClick={() => setShowPositionMenu(!showPositionMenu)}
                className="w-9 h-9 bg-white/50 hover:bg-white/80 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center"
              >
                <Settings className="w-5 h-5 text-gray-600 animate-spin-slow" />
              </button>

              {showPositionMenu && (
                <div className={`absolute ${sidebarPosition === 'top' ? 'top-full mt-2' : 'bottom-full mb-2'} right-0 bg-white rounded-xl shadow-2xl border border-gray-200 p-2 space-y-1 min-w-[160px] z-[10000] animate-fadeIn`}>
                  <button
                    onClick={() => {
                      setSidebarPosition('left');
                      setShowPositionMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === 'left' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm font-medium">Left</span>
                  </button>

                  <button
                    onClick={() => {
                      setSidebarPosition('right');
                      setShowPositionMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === 'right' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                  >
                    <ArrowRight className="w-4 h-4" />
                    <span className="text-sm font-medium">Right</span>
                  </button>

                  <button
                    onClick={() => {
                      setSidebarPosition('top');
                      setShowPositionMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === 'top' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                  >
                    <ArrowUp className="w-4 h-4" />
                    <span className="text-sm font-medium">Top</span>
                  </button>

                  <button
                    onClick={() => {
                      setSidebarPosition('bottom');
                      setShowPositionMenu(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors ${sidebarPosition === 'bottom' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-700'}`}
                  >
                    <ArrowDown className="w-4 h-4" />
                    <span className="text-sm font-medium">Bottom</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* COMPONENTS */

function Dropdown({ label, icon, open, onClick, items }) {
  return (
    <div className="space-y-1">
      <button
        onClick={onClick}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200
          ${open
            ? "bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-600 shadow-sm"
            : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50"
          }`}
      >
        <div className="flex items-center gap-3">
          <span className={`transition-transform ${open ? "text-indigo-600 scale-110" : "text-gray-500"}`}>{icon}</span>
          <span className="text-sm font-medium">{label}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-all duration-300 ${open ? "rotate-180 text-indigo-600" : "text-gray-500"}`}
        />
      </button>

      {open && items.length > 0 && (
        <div className="ml-6 pl-4 mt-1 space-y-0.5 border-l-2 border-indigo-200 animate-fadeIn">
          {items.map((item, i) => (
            <NavItem key={i} icon={item.icon} label={item.label} onClick={item.onClick} active={item.active} />
          ))}
        </div>
      )}
    </div>
  );
}

function NavItem({ icon, label, external, onClick, active }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl group transition-all duration-200
        ${active
          ? "bg-gradient-to-r from-indigo-500 to-blue-500 text-white shadow-lg shadow-indigo-500/30 scale-[0.98]"
          : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:scale-[0.98]"
        }`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`transition-transform group-hover:scale-110 ${active ? "text-white" : "text-gray-500"}`}
        >
          {icon}
        </span>
        <span className="text-sm font-medium whitespace-nowrap">{label}</span>
      </div>
      {external && (
        <ExternalLink
          className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-all ${active ? "text-white" : "text-gray-400"}`}
        />
      )}
    </button>
  );
}